import {
  CameraOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Form,
  Input,
  message,
  Modal,
  Upload,
} from "antd";
import { useEffect, useState } from "react";

export interface ProfileEditorValues {
  nickname: string;
  phone: string;
  mail: string;
}

interface ProfileEditorModalProps {
  avatarUrl: string;
  initialValues: ProfileEditorValues;
  onCancel: () => void;
  onSubmit: (
    values: ProfileEditorValues,
    avatarFile?: File,
    removeAvatar?: boolean
  ) => void | Promise<void>;
  open: boolean;
  submitting: boolean;
}

const MAX_AVATAR_SIZE = 2 * 1024 * 1024;
const ALLOWED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];

export default function ProfileEditorModal({
  avatarUrl,
  initialValues,
  onCancel,
  onSubmit,
  open,
  submitting,
}: ProfileEditorModalProps) {
  const [form] = Form.useForm<ProfileEditorValues>();
  const [avatarFile, setAvatarFile] = useState<File>();
  const [avatarPreview, setAvatarPreview] = useState("");
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const initialMail = initialValues.mail;
  const initialNickname = initialValues.nickname;
  const initialPhone = initialValues.phone;

  useEffect(() => {
    if (!open) {
      return;
    }
    form.setFieldsValue({
      mail: initialMail,
      nickname: initialNickname,
      phone: initialPhone,
    });
    setAvatarFile(undefined);
    setAvatarPreview(avatarUrl);
    setRemoveAvatar(false);
  }, [
    avatarUrl,
    form,
    initialMail,
    initialNickname,
    initialPhone,
    open,
  ]);

  function selectAvatar(file: File) {
    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      message.error("头像仅支持 JPG、PNG 或 WebP 格式");
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_AVATAR_SIZE) {
      message.error("头像图片不能超过 2MB");
      return Upload.LIST_IGNORE;
    }

    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(String(reader.result || ""));
    reader.readAsDataURL(file);
    setAvatarFile(file);
    setRemoveAvatar(false);
    return false;
  }

  function restoreDefaultAvatar() {
    setAvatarFile(undefined);
    setAvatarPreview("");
    setRemoveAvatar(true);
  }

  return (
    <Modal
      maskClosable={false}
      keyboard={false}
      className="profileEditorModal"
      destroyOnClose
      footer={null}
      onCancel={onCancel}
      open={open}
      title="编辑个人资料"
    >
      <Form<ProfileEditorValues>
        className="projectForm"
        form={form}
        layout="vertical"
        onFinish={(values) => onSubmit(values, avatarFile, removeAvatar)}
      >
        <div className="profileEditorAvatar">
          <Avatar
            icon={!avatarPreview ? <UserOutlined /> : undefined}
            size={88}
            src={avatarPreview || undefined}
          />
          <div className="profileEditorAvatar__content">
            <strong>个人头像</strong>
            <span>支持 JPG、PNG、WebP，图片不能超过 2MB</span>
            <div className="profileEditorAvatar__actions">
              <Upload
                accept=".jpg,.jpeg,.png,.webp"
                beforeUpload={selectAvatar}
                maxCount={1}
                showUploadList={false}
              >
                <span className="profileEditorAvatar__uploadAction">
                  <CameraOutlined aria-hidden />
                  选择头像
                </span>
              </Upload>
              <Button
                danger
                disabled={!avatarPreview}
                icon={<DeleteOutlined aria-hidden />}
                onClick={restoreDefaultAvatar}
              >
                恢复默认头像
              </Button>
            </div>
          </div>
        </div>

        <Form.Item
          label="邮箱"
          name="mail"
          normalize={(value) => String(value || "").trim()}
          rules={[
            { required: true, message: "请输入邮箱" },
            { type: "email", message: "请输入正确的邮箱格式" },
          ]}
        >
          <Input placeholder="请输入常用邮箱" />
        </Form.Item>
        <Form.Item
          label="昵称"
          name="nickname"
          rules={[
            { required: true, message: "请输入昵称" },
            { max: 30, message: "昵称不能超过 30 个字符" },
          ]}
        >
          <Input placeholder="请输入新的昵称" />
        </Form.Item>
        <Form.Item
          label="电话号码"
          name="phone"
          rules={[
            { required: true, message: "请输入电话号码" },
            { pattern: /^1\d{10}$/, message: "请输入正确的电话号码" },
          ]}
        >
          <Input placeholder="请输入企业钉钉绑定手机号" />
        </Form.Item>
        <div className="projectForm__footer">
          <Button onClick={onCancel}>取消</Button>
          <Button htmlType="submit" loading={submitting} type="primary">
            保存修改
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
